/**
 * AIPayloadBuilder
 *
 * Business logic service for formatting AI webhook request payloads.
 * Supports two modes:
 * - CREATE: Initial strategy generation (no current_block/prompt)
 * - UPDATE: Block regeneration (includes current_block + prompt)
 *
 * Related: Sprint 6, FR-057, FR-059
 * Reference: temp_plans/ai_strategy_creation_analysis.md
 */

import type { HydratedSourceData } from './SourceDataHydrator';

/**
 * AI webhook request mode
 */
export type AIWebhookMode = 'create' | 'update';

/**
 * Current block structure for UPDATE mode
 */
export interface CurrentBlock {
  blockId: string;
  content: Record<string, unknown>;
}

/**
 * AI webhook payload structure (FR-057)
 */
export interface AIWebhookPayload {
  mode: AIWebhookMode;
  sourceData: HydratedSourceData;
  current_block?: CurrentBlock;
  prompt?: string;
}

/**
 * Complete webhook request structure for backend /api/webhook-proxy
 */
export interface AIWebhookRequest {
  url: string;
  payload: AIWebhookPayload;
  action_description: string;
}

/**
 * Formats AI webhook request payloads for CREATE and UPDATE modes
 */
export class AIPayloadBuilder {
  /**
   * Build CREATE mode payload
   *
   * CREATE mode structure:
   * - mode: 'create'
   * - sourceData: hydrated audiences, tasks, studyCases
   * - NO current_block
   * - NO prompt
   *
   * @param hydratedData - Fully hydrated source data from SourceDataHydrator
   * @param webhookUrl - AI service webhook URL from Firestore settings
   * @returns Complete webhook request for /api/webhook-proxy
   */
  buildCreatePayload(
    hydratedData: HydratedSourceData,
    webhookUrl: string
  ): AIWebhookRequest {
    const payload: AIWebhookPayload = {
      mode: 'create',
      sourceData: hydratedData
    };

    return {
      url: webhookUrl,
      payload,
      action_description: 'Create strategy with AI'
    };
  }

  /**
   * Build UPDATE mode payload
   *
   * UPDATE mode structure:
   * - mode: 'update'
   * - sourceData: hydrated audiences, tasks, studyCases (same as CREATE)
   * - current_block: { blockId, content }
   * - prompt: user's change request
   *
   * @param hydratedData - Fully hydrated source data from SourceDataHydrator
   * @param currentBlock - Existing block to be regenerated
   * @param prompt - User's change request
   * @param webhookUrl - AI service webhook URL from Firestore settings
   * @returns Complete webhook request for /api/webhook-proxy
   */
  buildUpdatePayload(
    hydratedData: HydratedSourceData,
    currentBlock: CurrentBlock,
    prompt: string,
    webhookUrl: string
  ): AIWebhookRequest {
    const payload: AIWebhookPayload = {
      mode: 'update',
      sourceData: hydratedData,
      current_block: currentBlock,
      prompt
    };

    return {
      url: webhookUrl,
      payload,
      action_description: 'Regenerate block with AI'
    };
  }
}
